export type CatalogFolder = 'flows' | 'signals' | 'layouts';

/** ESP-IDF SPIFFS default max object name, excluding the trailing NUL. */
export const SPIFFS_MAX_PATH = 31;

export function slugCatalogName(name: string): string {
  const value = String(name || '').trim().replace(/[^A-Za-z0-9._-]/g, '_').replace(/^_+|_+$/g, '');
  return value.slice(0, 64) || 'default';
}

/** FNV-1a 32-bit, first 6 hex digits. Keep in sync with src/vcs/fs.cpp. */
export function fnv1aHex6(str: string): string {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0').slice(0, 6);
}

/** Device path that fits SPIFFS. Catalog ids may stay longer on the server. */
export function spiffsSafeCatalogPath(folder: CatalogFolder, name: string): string {
  const id = slugCatalogName(name);
  const prefix = `/${folder}/`;
  const ext = '.json';
  const budget = SPIFFS_MAX_PATH - prefix.length - ext.length;
  if (id.length <= budget) {
    return `${prefix}${id}${ext}`;
  }
  const hash = fnv1aHex6(id);
  const keep = Math.max(1, budget - hash.length - 1);
  return `${prefix}${id.slice(0, keep)}-${hash}${ext}`;
}

export function catalogPath(folder: CatalogFolder, name: string): string {
  return spiffsSafeCatalogPath(folder, name);
}

export function catalogNameFromPath(path: string, folder: CatalogFolder): string | null {
  const match = path.match(new RegExp(`^/${folder}/([A-Za-z0-9._-]+)\\.json$`, 'i'));
  return match ? match[1] : null;
}

export function listCatalogPaths(paths: string[], folder: CatalogFolder): string[] {
  return paths.filter((p) => p.startsWith(`/${folder}/`) && p.toLowerCase().endsWith('.json'));
}

export function nextCatalogName(folder: CatalogFolder, paths: string[]): string {
  const names = new Set(
    listCatalogPaths(paths, folder).map((p) => catalogNameFromPath(p, folder)).filter(Boolean) as string[],
  );
  if (!names.has('default')) {
    return 'default';
  }
  let index = 1;
  while (names.has(`${folder}${index}`)) {
    index += 1;
  }
  return `${folder}${index}`;
}
